import { EApplicantType, EFinancingProgram } from '@if/core/types'
import allApi from '../../api'
import { CopyCreditApplicationAsDraftDto } from '../../api/credit-application/CopyCreditApplicationAsDraftDto'
import { EditBankAccountDto } from '../../api/credit-application/EditBankAccountDto'
import { EditIncomesDto } from '../../api/credit-application/EditIncomesDto'
import { RefinanceCreditApplicationDto } from '../../api/credit-application/RefinanceCreditApplicationDto'
import {
  CreditApplication,
  CreditApplicationTask,
  CreditTaskComment,
  FullCreditApplication,
  PreviousApplication,
  ProofsOfRelease,
} from '../../types'
import { CreditApplicationReservation, CreditApplicationReservationDto } from '../../types/CreditApplicationReservation'
import { FinalCreditDecision } from '../../types/CreditDecisionSchema'
import { ExistingCustomerReference, SetCustomerReferenceDto } from '../../types/CustomerReferenceSchema'
import { CreditDecisionHistory, IncomesHistory } from '../../types/IncomesHistory'
import { LoanCustomer, SearchCustomerDto } from '../../types/LoanCustomerSchema'
import { PreviousApplicationInputDto } from '../../types/PreviousApplicationInputDto'
import { Refinancing } from '../../types/RefinancingSchema'
import { UpdateCreditApplicationMerchantDto } from '../../types/UpdateCreditApplicationMerchantDto'
import {
  makeDelete,
  makeDeleteWithReturn,
  makeDownloadWithObject,
  makeGetById,
  makeGetList,
  makeGetListWithIds,
  makeSave,
} from '../effects/standard-side-effects'
import {
  CHANGE_RUSH,
  CHANGE_STATUS,
  CONFIRM_ALL_INCOMES,
  COPY_CREDIT_APPLICATION_AS_DRAFT,
  CREATE_CREDIT_APPLICATION_LOCK,
  CREATE_CVT_NUMBER,
  CREATE_LOAN,
  creditSlice,
  DELETE_CREDIT_APPLICATION_LOCK,
  DOWNLOAD_CREDIT_PDF,
  DOWNLOAD_CVT_PDF,
  DOWNLOAD_FCF,
  GET_CREDIT_APPLICATION_LOCK,
  GET_CREDIT_BY_ID,
  GET_CREDITS_BY_FILTERS,
  GET_CURRENT_CREDIT_TASKS,
  GET_INCOMES_HISTORY,
  GET_PREVIOUS_APPLICANT_APPLICATIONS_BY_FILTER,
  GET_PREVIOUS_COAPPLICANT_APPLICATIONS_BY_FILTER,
  PAY_OUT,
  REFINANCE_CREDIT_APPLICATION,
  REMOVE_CREDIT_TASK,
  RESET_VOUCHED,
  SAVE_CREDIT,
  SAVE_CREDIT_TASK,
  SAVE_CREDIT_TASK_COMMENT,
  SAVE_FINAL_CREDIT_DECISION,
  SEND_FUND_DEPOSIT_CONFIRMATION,
  SET_EXISTING_APPLICANT_CUSTOMER_REFERENCE,
  SET_EXISTING_COAPPLICANT_CUSTOMER_REFERENCE,
  SET_PAYMENT_ACCOUNT,
  SET_PROOF_OF_RELEASE,
  SET_REFINANCING,
  UPDATE_CREDIT_APPLICATION_LOCK,
  UPDATE_INCOMES,
} from './credit-application-store'

const creditEffects = {
  getById: (id: string, financingProgramId: EFinancingProgram) =>
    makeGetById<CreditApplication>(GET_CREDIT_BY_ID, allApi.credit.getById, creditSlice.actions.setCurrent, {
      id,
      financingProgramId,
    }),
  create: (data: Partial<CreditApplication>) =>
    makeSave<CreditApplication>(SAVE_CREDIT, allApi.credit.create, creditSlice.actions.setCurrent, data),
  update: (data: Partial<CreditApplication>) =>
    makeSave<CreditApplication>(SAVE_CREDIT, allApi.credit.update, creditSlice.actions.setCurrent, data),
  getByFilters: (filters?: Record<string, string | number>) =>
    makeGetList<CreditApplication>(
      GET_CREDITS_BY_FILTERS,
      allApi.credit.getByFilters,
      creditSlice.actions.setList,
      filters,
    ),
  getCurrentCreditApplicationTasks: (creditApplicationId: string, financingProgramId: EFinancingProgram) =>
    makeGetListWithIds<CreditApplicationTask>(
      GET_CURRENT_CREDIT_TASKS,
      allApi.creditTask.getTaskByCreditApplicationId,
      creditSlice.actions.setCurrentCreditApplicationTasks,
      { creditApplicationId, financingProgramId },
    ),
  createTask: (data: Partial<CreditApplicationTask>) =>
    makeSave<CreditApplicationTask>(
      SAVE_CREDIT_TASK,
      allApi.creditTask.create,
      creditSlice.actions.setCreditTask,
      data,
    ),
  updateTask: (data: Partial<CreditApplicationTask>) =>
    makeSave<CreditApplicationTask>(
      SAVE_CREDIT_TASK,
      allApi.creditTask.update,
      creditSlice.actions.setCreditTask,
      data,
    ),
  updateMerchant: (data: Partial<UpdateCreditApplicationMerchantDto>) =>
    makeSave<UpdateCreditApplicationMerchantDto, CreditApplication>(
      SAVE_CREDIT,
      allApi.credit.updateCreditApplicationMerchant,
      creditSlice.actions.setCurrent,
      data,
    ),
  confirmAllIncomes: (data: Partial<CreditApplication>) =>
    makeSave<CreditApplication, CreditApplicationTask>(
      CONFIRM_ALL_INCOMES,
      allApi.credit.confirmAllIncomes,
      creditSlice.actions.setCreditTask,
      data,
    ),
  createTaskComment: (data: Partial<CreditTaskComment>) =>
    makeSave<CreditTaskComment>(
      SAVE_CREDIT_TASK_COMMENT,
      allApi.creditTaskComment.create,
      creditSlice.actions.setCreditTaskComment,
      data,
    ),
  saveCreditDecision: (data: Partial<FinalCreditDecision>) =>
    makeSave<FinalCreditDecision, CreditApplication>(
      SAVE_FINAL_CREDIT_DECISION,
      allApi.creditDecision.update,
      creditSlice.actions.setCurrent,
      data,
    ),
  deleteTask: (data: CreditApplicationTask, financingProgramId: EFinancingProgram) =>
    makeDelete<CreditApplicationTask>(
      REMOVE_CREDIT_TASK,
      allApi.creditTask.delete,
      creditSlice.actions.removeCreditTask,
      { ...data, financingProgramId },
    ),
  refreshCreditReport: (data: {
    creditApplicationId: string
    financingProgramId: EFinancingProgram
    isApplicant: boolean
    versionTag: string
    requestOriginId: string
  }) =>
    makeSave<{ creditApplicationId: string; isApplicant: boolean; versionTag: string }, CreditApplication>(
      GET_CREDIT_BY_ID,
      allApi.credit.refreshCreditReport,
      creditSlice.actions.setCurrent,
      data,
    ),
  changeStatus: (data: {
    financingProgramId: EFinancingProgram
    creditApplicationId: string
    status: string
    versionTag: string
  }) =>
    makeSave<{ status: string; versionTag: string }, CreditApplication>(
      CHANGE_STATUS,
      allApi.credit.changeStatus,
      creditSlice.actions.setCurrent,
      data,
    ),
  changeRush: (data: {
    financingProgramId: EFinancingProgram
    creditApplicationId: string
    isRush: boolean
    versionTag: string
  }) =>
    makeSave<{ rush: boolean; versionTag: string }, CreditApplication>(
      CHANGE_RUSH,
      allApi.credit.changeRush,
      creditSlice.actions.setCurrent,
      data,
    ),

  updateIncomes: (data: EditIncomesDto) =>
    makeSave<EditIncomesDto, CreditApplication>(
      UPDATE_INCOMES,
      allApi.credit.updateIncomes,
      creditSlice.actions.setCurrent,
      data,
    ),

  getPreviousApplicantApplication: (filter: PreviousApplicationInputDto) =>
    makeSave<PreviousApplicationInputDto, PreviousApplication[]>(
      GET_PREVIOUS_APPLICANT_APPLICATIONS_BY_FILTER,
      allApi.previousapplication.getByFilters,
      creditSlice.actions.setApplicantPreviousApplications,
      filter,
    ),
  getPreviousCoapplicantApplication: (filter: PreviousApplicationInputDto) =>
    makeSave<PreviousApplicationInputDto, PreviousApplication[]>(
      GET_PREVIOUS_COAPPLICANT_APPLICATIONS_BY_FILTER,
      allApi.previousapplication.getByFilters,
      creditSlice.actions.setCoapplicantPreviousApplications,
      filter,
    ),
  downloadCreditPdf: (creditApplication: CreditApplication) =>
    makeDownloadWithObject<CreditApplication>(DOWNLOAD_CREDIT_PDF, allApi.credit.downloadCreditPDF, creditApplication),

  getIncomesHistory: (creditApplicationId: string, financingProgramId: EFinancingProgram) =>
    makeGetById<IncomesHistory>(
      GET_INCOMES_HISTORY,
      allApi.history.getIncomesHistory,
      creditSlice.actions.setIncomesHistory,
      { creditApplicationId, financingProgramId },
    ),
  getCreditDecisionHistory: (creditApplicationId: string, financingProgramId: EFinancingProgram) =>
    makeGetListWithIds<CreditDecisionHistory>(
      GET_INCOMES_HISTORY,
      allApi.history.getCreditDecisionHistory,
      creditSlice.actions.setDecisionHistory,
      { creditApplicationId, financingProgramId },
    ),
  getCreditApplicationLock: (data: { financingProgramId: EFinancingProgram; creditApplicationId: string }) =>
    makeGetById(
      GET_CREDIT_APPLICATION_LOCK,
      allApi.credit.getCreditApplicationLock,
      creditSlice.actions.setCreditApplicationLock,
      data,
    ),

  lockCreditApplication: (data: { creditApplicationId: string; financingProgramId: EFinancingProgram }) =>
    makeSave<{ creditApplicationId: string }, CreditApplication>(
      UPDATE_CREDIT_APPLICATION_LOCK,
      allApi.credit.lockCreditApplication,
      creditSlice.actions.setCurrent,
      data,
    ),

  unlockCreditApplication: (data: { creditApplicationId: string; financingProgramId: EFinancingProgram }) =>
    makeSave<{ creditApplicationId: string }, CreditApplication>(
      UPDATE_CREDIT_APPLICATION_LOCK,
      allApi.credit.unlockCreditApplication,
      creditSlice.actions.setCurrent,
      data,
    ),

  payOut: (data: { creditApplicationId: string; financingProgramId: EFinancingProgram }) =>
    makeSave<{ creditApplicationId: string }>(PAY_OUT, allApi.credit.payOutCreditApplication, null, data),

  createCreditApplicationLock: (data: CreditApplicationReservationDto) =>
    makeSave<CreditApplicationReservationDto, CreditApplicationReservation>(
      CREATE_CREDIT_APPLICATION_LOCK,
      allApi.credit.createCreditApplicationLock,
      creditSlice.actions.setCreditApplicationLock,
      data,
    ),
  deleteCreditApplicationLock: (data: CreditApplicationReservation) =>
    makeDeleteWithReturn<CreditApplicationReservation>(
      DELETE_CREDIT_APPLICATION_LOCK,
      allApi.credit.deleteCreditApplicationLock,
      creditSlice.actions.setCreditApplicationLock,
      data,
    ),
  updateCreditApplicationLock: (data: CreditApplicationReservation) =>
    makeSave<CreditApplicationReservation>(
      UPDATE_CREDIT_APPLICATION_LOCK,
      allApi.credit.updateCreditApplicationLock,
      creditSlice.actions.setCreditApplicationLock,
      data,
    ),
  createCvtNumber: (data: { id: string; financingProgramId: EFinancingProgram }) =>
    makeSave<CreditApplication>(
      CREATE_CVT_NUMBER,
      allApi.contract.create(data.id, data.financingProgramId),
      creditSlice.actions.setCurrent,
      data,
    ),
  downloadCvtPdf: (data: { id: string; financingProgramId: EFinancingProgram }) =>
    makeDownloadWithObject(DOWNLOAD_CVT_PDF, allApi.contract.downloadCvtPDF(data.id, data.financingProgramId), data),

  setRefinancing: (data: Refinancing) =>
    makeSave<Refinancing, CreditApplication>(
      SET_REFINANCING,
      allApi.refinancing.update,
      creditSlice.actions.setCurrent,
      data,
    ),

  setApplicantCustomerReference: (data: SetCustomerReferenceDto) =>
    makeSave<ExistingCustomerReference, CreditApplication>(
      SET_EXISTING_APPLICANT_CUSTOMER_REFERENCE,
      allApi.customerReference.setApplicantCustomerReference,
      creditSlice.actions.setCurrent,
      data,
    ),

  setCoapplicantCustomerReference: (data: SetCustomerReferenceDto) =>
    makeSave<ExistingCustomerReference, CreditApplication>(
      SET_EXISTING_COAPPLICANT_CUSTOMER_REFERENCE,
      allApi.customerReference.setCoapplicantCustomerReference,
      creditSlice.actions.setCurrent,
      data,
    ),

  findCustomers: (data: SearchCustomerDto) =>
    makeSave<SearchCustomerDto, LoanCustomer[]>(
      SET_EXISTING_COAPPLICANT_CUSTOMER_REFERENCE,
      allApi.loanCustomer.findCustomers,
      null,
      data,
    ),

  updateProofOfRelease: (data: ProofsOfRelease) =>
    makeSave<ProofsOfRelease, CreditApplication>(
      SET_PROOF_OF_RELEASE,
      allApi.credit.updateProofOfRelease,
      creditSlice.actions.setCurrent,
      data,
    ),

  createLoan: (data: { id: string; financingProgramId: EFinancingProgram }) =>
    makeSave<CreditApplication>(
      CREATE_LOAN,
      allApi.loan.create(data.financingProgramId, data.id),
      creditSlice.actions.setCurrent,
      data,
    ),
  setPaymentAccount: (data: EditBankAccountDto) =>
    makeSave<EditBankAccountDto, FullCreditApplication>(
      SET_PAYMENT_ACCOUNT,
      allApi.credit.setPaymentAccount,
      creditSlice.actions.setPaymentAccount,
      data,
    ),

  sendFundDepositConfirmation: (data: { creditApplicationId: string; financingProgramId: EFinancingProgram }) =>
    makeSave<{ creditApplicationId: string; financingProgramId: EFinancingProgram }>(
      SEND_FUND_DEPOSIT_CONFIRMATION,
      allApi.credit.sendFundDepositConfirmation,
      null,
      data,
    ),

  downloadFCFDocument: (data: { creditApplicationId: string; financingProgramId: EFinancingProgram }) =>
    makeDownloadWithObject<{ creditApplicationId: string; financingProgramId: EFinancingProgram }>(
      DOWNLOAD_FCF,
      allApi.document.downloadFCFDocument(data.creditApplicationId, data.financingProgramId),
      data,
    ),

  refinanceCreditApplication: (data: RefinanceCreditApplicationDto) =>
    makeSave<CreditApplication>(REFINANCE_CREDIT_APPLICATION, allApi.credit.refinanceCreditApplication, null, data),
  copyCreditApplicationAsDraft: (data: CopyCreditApplicationAsDraftDto) =>
    makeSave<CreditApplication>(
      COPY_CREDIT_APPLICATION_AS_DRAFT,
      allApi.credit.copyCreditApplicationAsDraft,
      null,
      data,
    ),
  resetVouched: (data: {
    creditApplicationId: string
    financingProgramId: EFinancingProgram
    applicantType: EApplicantType
  }) =>
    makeSave<{ creditApplicationId: string; financingProgramId: EFinancingProgram; applicantType: EApplicantType }>(
      RESET_VOUCHED,
      allApi.credit.resetVouched,
      null,
      data,
    ),
}

export default creditEffects
